/**
 * DayCell component
 * Individual day cell with lunch/dinner toggles for availability calendar
 */

import { useTranslation } from 'react-i18next';
import { formatDayNameAndNumber } from '@/lib/utils/date';

interface DayCellProps {
  date: Date;
  lunchChecked: boolean;
  dinnerChecked: boolean;
  onLunchChange: (checked: boolean) => void;
  onDinnerChange: (checked: boolean) => void;
  isToday?: boolean;
  isPast?: boolean;
}

/**
 * DayCell component
 *
 * Features:
 * - Display day name and date
 * - Toggle lunch presence
 * - Toggle dinner presence
 * - Highlight today
 * - Responsive design
 *
 * @example
 * ```tsx
 * <DayCell
 *   date={new Date()}
 *   lunchChecked={true}
 *   dinnerChecked={false}
 *   onLunchChange={(checked) => console.log('Lunch:', checked)}
 *   onDinnerChange={(checked) => console.log('Dinner:', checked)}
 *   isToday={true}
 * />
 * ```
 */
export function DayCell({
  date,
  lunchChecked,
  dinnerChecked,
  onLunchChange,
  onDinnerChange,
  isToday,
  isPast = false,
}: DayCellProps) {
  const { t } = useTranslation();
  const { dayName, dayNumber } = formatDayNameAndNumber(date);

  return (
    <div
      className={`bg-white rounded-lg shadow-md p-4 transition-opacity ${
        isToday ? 'ring-2 ring-primary-500' : ''
      } ${isPast ? 'opacity-50' : ''}`}
    >
      {/* Day header */}
      <div className="text-center mb-4">
        <div className="text-sm font-medium text-gray-600">{dayName}</div>
        <div
          className={`text-2xl font-bold mt-1 ${
            isToday ? 'text-primary-600' : 'text-gray-900'
          }`}
        >
          {dayNumber}
        </div>
      </div>

      {/* Meal toggles */}
      <div className="space-y-3">
        {/* Lunch toggle */}
        <label className={`flex items-center gap-3 ${isPast ? 'cursor-not-allowed' : 'cursor-pointer'} group`}>
          <input
            type="checkbox"
            checked={lunchChecked}
            onChange={(e) => onLunchChange(e.target.checked)}
            disabled={isPast}
            className={`h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
              isPast ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          />
          <span className={`text-sm font-medium text-gray-700 ${!isPast && 'group-hover:text-gray-900'}`}>
            {t('availability.lunch')}
          </span>
        </label>

        {/* Dinner toggle */}
        <label className={`flex items-center gap-3 ${isPast ? 'cursor-not-allowed' : 'cursor-pointer'} group`}>
          <input
            type="checkbox"
            checked={dinnerChecked}
            onChange={(e) => onDinnerChange(e.target.checked)}
            disabled={isPast}
            className={`h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
              isPast ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          />
          <span className={`text-sm font-medium text-gray-700 ${!isPast && 'group-hover:text-gray-900'}`}>
            {t('availability.dinner')}
          </span>
        </label>
      </div>

      {/* Visual indicator for no selection */}
      {!lunchChecked && !dinnerChecked && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">{t('availability.notPresent')}</p>
        </div>
      )}
    </div>
  );
}
